import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import TableBulkEditButton from "common/Components/TableBulkEditButton";
import TableEditableCell from "common/Components/TableEditableCell";
import TableExportCsv from "common/Components/TableExportCsv";
import { downloadCsv } from "common/utils/csvExport";
import {
  CellSelect,
  CellTextField,
  CheckboxCheckMark,
  CheckboxCheckedIcon,
  CheckboxIndeterminateIcon,
  CheckboxTableCell,
  CheckboxUncheckedIcon,
  DataTableLayout,
  EmptyStateText,
  FilterEmptyDash,
  TableBodyLoader,
  TableScrollShell,
  FilterSelectCheckIcon,
  FooterSelectionStack,
  FooterSpacer,
  PaginationWrapper,
  SelectionSummary,
  SortBothIcon,
  StyledFilterCell,
  StyledHeaderCell,
  StyledPaper,
  StyledTable,
  StyledTableContainer,
  StyledTablePagination,
  StyledToolbar,
  TableCheckbox,
  TableFooterBar,
  ToolbarActions,
  ToolbarTitle,
  ToolbarTitleAccessory,
  ToolbarTitleGroup,
  VisuallyHidden,
  filterSelectMenuItemSelectedStrongSx,
  filterSelectMenuProps,
  paginationSelectMenuProps,
  SelectChevronIcon,
} from "./DataTable.styles";

export const FILTER_SELECT_ALL_SENTINEL = "__all__";

const TABLE_LOADING_ROWS_LABEL = "Loading rows…";

const formatCount = (value) => Number(value || 0).toLocaleString("en-US");

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function TableSortIcon({ active, direction, className }) {
  const glyph = !active ? "↕" : direction === "desc" ? "↓" : "↑";
  return (
    <SortBothIcon className={className} aria-hidden>
      {glyph}
    </SortBothIcon>
  );
}

TableSortIcon.propTypes = {
  active: PropTypes.bool,
  direction: PropTypes.oneOf(["asc", "desc"]),
  className: PropTypes.string,
};

TableSortIcon.defaultProps = {
  active: false,
  direction: "asc",
  className: undefined,
};

function renderTableCheckbox(props) {
  return (
    <TableCheckbox
      {...props}
      icon={<CheckboxUncheckedIcon />}
      checkedIcon={
        <CheckboxCheckedIcon>
          <CheckboxCheckMark />
        </CheckboxCheckedIcon>
      }
      indeterminateIcon={<CheckboxIndeterminateIcon />}
      size="small"
      disableRipple
    />
  );
}

const normalizeSelectFilterValue = (value) => {
  if (
    value == null ||
    value === "" ||
    value === FILTER_SELECT_ALL_SENTINEL
  ) {
    return FILTER_SELECT_ALL_SENTINEL;
  }
  return String(value);
};

function descendingComparator(rowA, rowB, sortProperty) {
  const valueA = rowA[sortProperty];
  const valueB = rowB[sortProperty];
  if (valueA == null && valueB == null) return 0;
  if (valueA == null) return 1;
  if (valueB == null) return -1;
  if (typeof valueA === "number" && typeof valueB === "number") {
    return valueB - valueA;
  }
  return String(valueB).localeCompare(String(valueA), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function getComparator(sortOrder, sortProperty) {
  return sortOrder === "desc"
    ? (rowA, rowB) => descendingComparator(rowA, rowB, sortProperty)
    : (rowA, rowB) => -descendingComparator(rowA, rowB, sortProperty);
}

function stableSort(rowArray, comparator) {
  const rowsWithIndex = rowArray.map((row, index) => [row, index]);
  rowsWithIndex.sort((pairA, pairB) => {
    const comparison = comparator(pairA[0], pairB[0]);
    if (comparison !== 0) return comparison;
    return pairA[1] - pairB[1];
  });
  return rowsWithIndex.map((pair) => pair[0]);
}

export default function DataTable({
  title = "",
  columns,
  rows,
  getRowId,
  defaultRowsPerPage,
  selectable,
  onBulkEdit,
  bulkEditLabel,
  isBulkEditAllowed,
  bulkEditBlockedTooltip,
  exportFileName,
  onExportCsv,
  showExport,
  onRowUpdate,
  onRowClick,
  dense,
  testId,
  onFilterChange,
  applyTextFiltersOnCommit,
  serverMode,
  totalRows,
  page,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  exportDisabled,
  exportDisabledTitle,
  exportLoading,
  exportToolbarAction,
  toolbarActions,
  titleAccessory,
  filterValues,
  loading = false,
  hideToolbar = false,
  renderEmptyState,
  defaultSortProperty = "",
  defaultSortOrder = "asc",
  tableMinWidth,
}) {
  const [sortOrder, setSortOrder] = useState(defaultSortOrder);
  const [sortProperty, setSortProperty] = useState(defaultSortProperty);
  const [columnFilters, setColumnFilters] = useState(() => filterValues ?? {});
  const [draftTextFilters, setDraftTextFilters] = useState(() => filterValues ?? {});

  useEffect(() => {
    if (filterValues == null) {
      return;
    }
    setColumnFilters(filterValues);
    if (applyTextFiltersOnCommit) {
      setDraftTextFilters(filterValues);
    }
  }, [filterValues, applyTextFiltersOnCommit]);
  const [pageIndex, setPageIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [selectedRowIds, setSelectedRowIds] = useState({});
  const activePage = serverMode ? page : pageIndex;
  const activeRowsPerPage = rowsPerPage;

  const filteredRows = useMemo(() => {
    if (serverMode) {
      return rows;
    }
    return rows.filter((row) => {
      for (const column of columns) {
        const columnFilter = column.filter;
        if (!columnFilter) continue;
        const filterRawValue = columnFilters[column.id];
        if (
          filterRawValue == null ||
          filterRawValue === "" ||
          filterRawValue === FILTER_SELECT_ALL_SENTINEL
        ) {
          continue;
        }
        const cellValue = row[column.id];
        const cellString =
          cellValue == null
            ? ""
            : typeof cellValue === "string"
              ? cellValue
              : String(cellValue);
        if (columnFilter.type === "text") {
          if (
            !cellString
              .toLowerCase()
              .includes(String(filterRawValue).toLowerCase())
          ) {
            return false;
          }
        } else if (columnFilter.type === "select") {
          if (String(cellValue) !== String(filterRawValue)) return false;
        }
      }
      return true;
    });
  }, [rows, columns, columnFilters]);

  const sortedRows = useMemo(() => {
    if (serverMode) {
      return filteredRows;
    }
    if (!sortProperty) return filteredRows;
    return stableSort(filteredRows, getComparator(sortOrder, sortProperty));
  }, [filteredRows, sortOrder, sortProperty, serverMode]);

  const pageRows = useMemo(() => {
    if (serverMode) {
      return sortedRows;
    }
    const startIndex = activePage * activeRowsPerPage;
    return sortedRows.slice(startIndex, startIndex + activeRowsPerPage);
  }, [sortedRows, activePage, activeRowsPerPage, serverMode]);

  const selectedCountInFilteredView = useMemo(() => {
    const allowedIds = new Set(sortedRows.map((r) => String(getRowId(r))));
    let count = 0;
    Object.keys(selectedRowIds).forEach((id) => {
      if (selectedRowIds[id] && allowedIds.has(String(id))) count += 1;
    });
    return count;
  }, [sortedRows, selectedRowIds, getRowId]);

  const selectedRowsForBulk = useMemo(() => {
    const selectedIds = Object.keys(selectedRowIds).filter(
      (key) => selectedRowIds[key]
    );
    return sortedRows.filter((r) =>
      selectedIds.includes(String(getRowId(r)))
    );
  }, [sortedRows, selectedRowIds, getRowId]);

  const bulkEditBlockedByRule = Boolean(
    onBulkEdit &&
    typeof isBulkEditAllowed === "function" &&
    selectedCountInFilteredView > 0 &&
    !isBulkEditAllowed(selectedRowsForBulk)
  );

  const bulkEditButtonDisabled =
    selectedCountInFilteredView === 0 || bulkEditBlockedByRule;

  const bulkEditButtonTitle =
    bulkEditBlockedByRule && bulkEditBlockedTooltip
      ? bulkEditBlockedTooltip
      : "";

  const handleSortRequest = (property) => {
    const isAscending = sortProperty === property && sortOrder === "asc";
    const nextSortOrder = isAscending ? "desc" : "asc";
    setSortOrder(nextSortOrder);
    setSortProperty(property);
    if (serverMode) {
      onSortChange?.(property, nextSortOrder);
    }
  };

  const handleColumnFilterChange = (columnId, nextValue) => {
    const column = columns.find((entry) => entry.id === columnId);
    const normalizedValue =
      column?.filter?.type === "select"
        ? normalizeSelectFilterValue(nextValue)
        : nextValue;
    setColumnFilters((previous) => {
      const nextFilters = { ...previous, [columnId]: normalizedValue };
      onFilterChange?.(nextFilters, columnId);
      return nextFilters;
    });
    if (applyTextFiltersOnCommit && column?.filter?.type === "text") {
      setDraftTextFilters((previous) => ({
        ...previous,
        [columnId]: normalizedValue,
      }));
    }
    if (!serverMode) {
      setPageIndex(0);
    }
  };

  const commitTextFilter = (columnId) => {
    const nextValue = draftTextFilters[columnId] ?? "";
    if ((columnFilters[columnId] ?? "") === nextValue) return;
    handleColumnFilterChange(columnId, nextValue);
  };

  const handleSelectAllOnPage = (event) => {
    if (!selectable) return;
    if (event.target.checked) {
      const next = { ...selectedRowIds };
      pageRows.forEach((row) => {
        next[String(getRowId(row))] = true;
      });
      setSelectedRowIds(next);
      return;
    }
    const next = { ...selectedRowIds };
    pageRows.forEach((row) => {
      delete next[String(getRowId(row))];
    });
    setSelectedRowIds(next);
  };

  const handleToggleRowSelection = (row) => {
    const id = String(getRowId(row));
    setSelectedRowIds((previous) => ({ ...previous, [id]: !previous[id] }));
  };

  const allRowsOnPageSelected =
    selectable &&
    pageRows.length > 0 &&
    pageRows.every((r) => selectedRowIds[String(getRowId(r))]);
  const someRowsOnPageSelected =
    selectable && pageRows.some((r) => selectedRowIds[String(getRowId(r))]);

  const handleExportFilteredSortedRows = useCallback(() => {
    const headerLabels = columns.map((c) => c.label);
    const columnIds = columns.map((c) => c.id);
    const data = sortedRows.map((row) => {
      const rowObject = {};
      headerLabels.forEach((label, index) => {
        const value = row[columnIds[index]];
        rowObject[label] = value == null ? "" : String(value);
      });
      return rowObject;
    });
    downloadCsv(exportFileName || "export.csv", headerLabels, data);
  }, [columns, sortedRows, exportFileName]);

  const handleExportClick = useCallback(() => {
    if (typeof onExportCsv === "function") {
      onExportCsv();
      return;
    }
    handleExportFilteredSortedRows();
  }, [onExportCsv, handleExportFilteredSortedRows]);

  const handleBulkEditClick = () => {
    if (!onBulkEdit) return;
    if (
      typeof isBulkEditAllowed === "function" &&
      !isBulkEditAllowed(selectedRowsForBulk)
    ) {
      return;
    }
    onBulkEdit(selectedRowsForBulk);
  };

  const renderDataCell = (column, row) => {
    const cellValue = row[column.id];
    const resolvedEditable =
      typeof column.editable === "function"
        ? column.editable(row)
        : column.editable;
    if (column.render) {
      return column.render(cellValue, row, {
        onChange: (nextValue) =>
          onRowUpdate?.(getRowId(row), column.id, nextValue),
      });
    }
    if (resolvedEditable === "text") {
      return (
        <TableEditableCell
          fieldType="text"
          columnLabel={column.label}
          value={cellValue}
          onChange={(nextValue) =>
            onRowUpdate?.(getRowId(row), column.id, nextValue)
          }
        />
      );
    }
    if (resolvedEditable === "select") {
      const rowSelectOptions =
        typeof column.selectOptions === "function"
          ? column.selectOptions(row)
          : (column.selectOptions ?? []);
      return (
        <TableEditableCell
          fieldType="select"
          columnLabel={column.label}
          value={cellValue}
          selectOptions={rowSelectOptions}
          onChange={(nextValue) =>
            onRowUpdate?.(getRowId(row), column.id, nextValue)
          }
        />
      );
    }
    return (
      <TableEditableCell
        fieldType="display"
        columnLabel={column.label}
        value={cellValue}
      />
    );
  };

  return (
    <DataTableLayout>
    <StyledPaper elevation={0} data-testid={testId}>
      {!hideToolbar ? (
        <StyledToolbar>
          {title.length > 0 || titleAccessory ? (
            <ToolbarTitleGroup>
              {title.length > 0 ? (
                <ToolbarTitle component="h2">{title}</ToolbarTitle>
              ) : null}
              {titleAccessory ? (
                <ToolbarTitleAccessory>{titleAccessory}</ToolbarTitleAccessory>
              ) : null}
            </ToolbarTitleGroup>
          ) : null}
          <ToolbarActions>
            <TableBulkEditButton
              visible={Boolean(selectable)}
              disabled={bulkEditButtonDisabled}
              label={bulkEditLabel(selectedCountInFilteredView, selectedRowsForBulk)}
              title={bulkEditButtonTitle}
              onClick={handleBulkEditClick}
            />
            {showExport
              ? exportToolbarAction ?? (
                <TableExportCsv
                  visible
                  onSimpleExport={handleExportClick}
                  disabled={Boolean(exportDisabled)}
                  disabledTitle={exportDisabledTitle}
                  loading={Boolean(exportLoading)}
                />
              )
              : null}
            {toolbarActions}
          </ToolbarActions>
        </StyledToolbar>
      ) : null}

      <TableScrollShell>
        {loading ? (
          <TableBodyLoader>
            <CircularProgress size={32} aria-label="Loading table" />
            {TABLE_LOADING_ROWS_LABEL}
          </TableBodyLoader>
        ) : null}
        <StyledTableContainer>
          <StyledTable size={dense ? "small" : "medium"} $minWidth={tableMinWidth}>
          <colgroup>
            {selectable ? <col style={{ width: 44 }} /> : null}
            {columns.map((column) => (
              <col
                key={column.id}
                style={
                  column.width != null
                    ? { width: column.width }
                    : column.minWidth != null
                      ? { width: column.minWidth }
                      : undefined
                }
              />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {selectable ? (
                <CheckboxTableCell padding="checkbox">
                  {renderTableCheckbox({
                    indeterminate:
                      someRowsOnPageSelected && !allRowsOnPageSelected,
                    checked: allRowsOnPageSelected,
                    onChange: handleSelectAllOnPage,
                    inputProps: { "aria-label": "select all on page" },
                  })}
                </CheckboxTableCell>
              ) : null}
              {columns.map((column) => {
                const isSorted = sortProperty === column.id;
                return (
                  <StyledHeaderCell
                    key={column.id}
                    align={column.align ?? "left"}
                    sortDirection={isSorted ? sortOrder : false}
                    $minWidth={column.minWidth}
                    $width={column.width}
                  >
                    {column.sortable !== false ? (
                      <TableSortLabel
                        active={isSorted}
                        direction={isSorted ? sortOrder : "asc"}
                        onClick={() => handleSortRequest(column.id)}
                        IconComponent={(iconProps) => (
                          <TableSortIcon
                            {...iconProps}
                            active={isSorted}
                            direction={isSorted ? sortOrder : "asc"}
                          />
                        )}
                      >
                        {column.label}
                        {isSorted ? (
                          <VisuallyHidden>
                            {sortOrder === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </VisuallyHidden>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </StyledHeaderCell>
                );
              })}
            </TableRow>
            <TableRow>
              {selectable ? <CheckboxTableCell padding="checkbox" /> : null}
              {columns.map((column) => (
                <StyledFilterCell key={`filter-${column.id}`}>
                  {column.filter?.type === "text" ? (
                    <CellTextField
                      size="small"
                      fullWidth
                      placeholder="Filter"
                      value={
                        applyTextFiltersOnCommit
                          ? (draftTextFilters[column.id] ?? columnFilters[column.id] ?? "")
                          : (columnFilters[column.id] ?? "")
                      }
                      onChange={(e) => {
                        if (applyTextFiltersOnCommit) {
                          const nextValue = e.target.value;
                          setDraftTextFilters((previous) => ({
                            ...previous,
                            [column.id]: nextValue,
                          }));
                          return;
                        }
                        handleColumnFilterChange(column.id, e.target.value);
                      }}
                      onBlur={() => {
                        if (applyTextFiltersOnCommit) {
                          commitTextFilter(column.id);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (applyTextFiltersOnCommit && event.key === "Enter") {
                          event.preventDefault();
                          commitTextFilter(column.id);
                        }
                      }}
                      inputProps={{
                        "aria-label": `Filter ${column.label}`,
                        "data-testid": `filter-${column.id}`,
                      }}
                    />
                  ) : null}
                  {column.filter?.type === "select" ? (
                    <CellSelect
                      size="small"
                      fullWidth
                      displayEmpty
                      IconComponent={SelectChevronIcon}
                      value={normalizeSelectFilterValue(
                        columnFilters[column.id]
                      )}
                      onChange={(e) =>
                        handleColumnFilterChange(column.id, e.target.value)
                      }
                      MenuProps={filterSelectMenuProps}
                      renderValue={(selected) => {
                        if (
                          selected == null ||
                          selected === "" ||
                          selected === FILTER_SELECT_ALL_SENTINEL
                        ) {
                          return "All";
                        }
                        const match = (column.filter.options ?? []).find(
                          (opt) => String(opt.value) === String(selected)
                        );
                        return match?.label ?? String(selected);
                      }}
                      inputProps={{
                        "aria-label": `Filter ${column.label}`,
                        "data-testid": `filter-${column.id}`,
                      }}
                    >
                      <MenuItem
                        value={FILTER_SELECT_ALL_SENTINEL}
                        sx={filterSelectMenuItemSelectedStrongSx}
                      >
                        <FilterSelectCheckIcon
                          $visible={
                            normalizeSelectFilterValue(
                              columnFilters[column.id]
                            ) === FILTER_SELECT_ALL_SENTINEL
                          }
                        />
                        All
                      </MenuItem>
                      {(column.filter.options ?? []).map((opt) => {
                        const isSelected =
                          String(
                            normalizeSelectFilterValue(columnFilters[column.id])
                          ) === String(opt.value);
                        return (
                          <MenuItem
                            key={String(opt.value)}
                            value={String(opt.value)}
                            sx={filterSelectMenuItemSelectedStrongSx}
                          >
                            <FilterSelectCheckIcon $visible={isSelected} />
                            {opt.label}
                          </MenuItem>
                        );
                      })}
                    </CellSelect>
                  ) : null}
                  {!column.filter ? <FilterEmptyDash>—</FilterEmptyDash> : null}
                </StyledFilterCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              pageRows.map((row, rowIndex) => {
                const rowId = String(getRowId(row));
                const isRowSelected = !!selectedRowIds[rowId];
                return (
                  <TableRow
                    key={`${rowId}-${rowIndex}`}
                    hover
                    selected={Boolean(selectable && isRowSelected)}
                    onClick={() => onRowClick?.(row)}
                    sx={onRowClick ? { cursor: "pointer" } : undefined}
                  >
                    {selectable ? (
                      <CheckboxTableCell
                        padding="checkbox"
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                      >
                        {renderTableCheckbox({
                          checked: isRowSelected,
                          onChange: (event) => {
                            event.stopPropagation();
                            handleToggleRowSelection(row);
                          },
                          inputProps: { "aria-label": `select row ${rowId}` },
                        })}
                      </CheckboxTableCell>
                    ) : null}
                    {columns.map((column) => (
                      <TableCell key={column.id} align={column.align ?? "left"}>
                        {renderDataCell(column, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            {!loading && pageRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  align="center"
                >
                  {typeof renderEmptyState === "function" ? (
                    renderEmptyState()
                  ) : (
                    <EmptyStateText>
                      No rows match the current filters.
                    </EmptyStateText>
                  )}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </StyledTable>
        </StyledTableContainer>
      </TableScrollShell>

      <TableFooterBar>
        {selectable ? (
          <FooterSelectionStack>
            <SelectionSummary variant="body2">
              Selected in filtered view: {formatCount(selectedCountInFilteredView)}/
              {formatCount(serverMode ? totalRows : sortedRows.length)}
            </SelectionSummary>
            {bulkEditBlockedByRule && bulkEditBlockedTooltip ? (
              <Typography variant="caption" color="text.secondary" component="p">
                {bulkEditBlockedTooltip}
              </Typography>
            ) : null}
          </FooterSelectionStack>
        ) : (
          <FooterSpacer />
        )}
        <PaginationWrapper>
          <StyledTablePagination
            component="div"
            count={serverMode ? totalRows : sortedRows.length}
            page={activePage}
            onPageChange={(_, nextPage) => {
              if (serverMode) {
                onPageChange?.(nextPage);
              } else {
                setPageIndex(nextPage);
              }
            }}
            rowsPerPage={activeRowsPerPage}
            onRowsPerPageChange={(e) => {
              const nextRowsPerPage = parseInt(e.target.value, 10);
              setRowsPerPage(nextRowsPerPage);
              if (serverMode) {
                onRowsPerPageChange?.(nextRowsPerPage);
              } else {
                setPageIndex(0);
              }
            }}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            labelDisplayedRows={({ from, to, count }) =>
              `${formatCount(from)}–${formatCount(to)} of ${count !== -1 ? formatCount(count) : `more than ${formatCount(to)}`
              }`
            }
            SelectProps={{
              inputProps: { "aria-label": "Rows per page" },
              native: false,
              IconComponent: SelectChevronIcon,
              MenuProps: paginationSelectMenuProps,
            }}
          />
        </PaginationWrapper>
      </TableFooterBar>
    </StyledPaper>
    </DataTableLayout>
  );
}

DataTable.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      align: PropTypes.oneOf(["left", "right", "center"]),
      minWidth: PropTypes.number,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      filter: PropTypes.shape({
        type: PropTypes.oneOf(["text", "select"]).isRequired,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
              .isRequired,
            label: PropTypes.string.isRequired,
          })
        ),
      }),
      editable: PropTypes.oneOfType([
        PropTypes.oneOf([false, "text", "select"]),
        PropTypes.func,
      ]),
      selectOptions: PropTypes.oneOfType([
        PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
          })
        ),
        PropTypes.func,
      ]),
      render: PropTypes.func,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  getRowId: PropTypes.func.isRequired,
  defaultRowsPerPage: PropTypes.number,
  selectable: PropTypes.bool,
  onBulkEdit: PropTypes.func,
  bulkEditLabel: PropTypes.func,
  isBulkEditAllowed: PropTypes.func,
  bulkEditBlockedTooltip: PropTypes.string,
  exportFileName: PropTypes.string,
  onExportCsv: PropTypes.func,
  showExport: PropTypes.bool,
  onRowUpdate: PropTypes.func,
  onRowClick: PropTypes.func,
  dense: PropTypes.bool,
  testId: PropTypes.string,
  onFilterChange: PropTypes.func,
  applyTextFiltersOnCommit: PropTypes.bool,
  serverMode: PropTypes.bool,
  totalRows: PropTypes.number,
  page: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  exportDisabled: PropTypes.bool,
  exportDisabledTitle: PropTypes.string,
  exportLoading: PropTypes.bool,
  exportToolbarAction: PropTypes.node,
  toolbarActions: PropTypes.node,
  titleAccessory: PropTypes.node,
  filterValues: PropTypes.object,
  loading: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  renderEmptyState: PropTypes.func,
  defaultSortProperty: PropTypes.string,
  defaultSortOrder: PropTypes.oneOf(["asc", "desc"]),
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

DataTable.defaultProps = {
  title: "",
  defaultRowsPerPage: 10,
  selectable: false,
  onBulkEdit: undefined,
  bulkEditLabel: (count) => `Bulk Edit (${count})`,
  isBulkEditAllowed: undefined,
  bulkEditBlockedTooltip: "",
  exportFileName: "export.csv",
  onExportCsv: undefined,
  showExport: true,
  onRowUpdate: undefined,
  onRowClick: undefined,
  dense: true,
  testId: "data-table",
  onFilterChange: undefined,
  applyTextFiltersOnCommit: false,
  serverMode: false,
  totalRows: 0,
  page: 0,
  onPageChange: undefined,
  onRowsPerPageChange: undefined,
  onSortChange: undefined,
  exportDisabled: false,
  exportDisabledTitle: "",
  exportLoading: false,
  exportToolbarAction: undefined,
  toolbarActions: null,
  titleAccessory: undefined,
  filterValues: undefined,
  loading: false,
  hideToolbar: false,
  renderEmptyState: undefined,
  defaultSortProperty: "",
  defaultSortOrder: "asc",
  tableMinWidth: undefined,
};
